import Color from 'colorjs.io';

export type GradientSpace = 'oklab' | 'oklch';

export interface GradientStopInput {
  id?: string;
  color: string;
  position: number;
}

export interface GradientStop {
  id: string;
  color: string;
  position: number;
}

export interface GradientBuildOptions {
  space?: GradientSpace;
  sampleCount?: number;
  width?: number;
  height?: number;
  gradientId?: string;
}

export interface GradientBuildResult {
  stops: GradientStop[];
  css: string;
  svg: string;
  samples: string[];
}

const DEFAULT_SAMPLE_COUNT = 16;
const DEFAULT_WIDTH = 600;
const DEFAULT_HEIGHT = 120;

const clamp01 = (value: number) => {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.min(Math.max(value, 0), 1);
};

const toClampedSrgbHex = (color: Color): string => {
  const srgb = color.to('srgb');
  const coords = srgb.coords as [number?, number?, number?];
  const normalize = (component?: number) => {
    if (!Number.isFinite(component ?? 0)) {
      return 0;
    }
    const value = component ?? 0;
    return Math.min(Math.max(value, 0), 1);
  };
  const r = normalize(coords[0]);
  const g = normalize(coords[1]);
  const b = normalize(coords[2]);
  const clamped = new Color('srgb', [r, g, b], srgb.alpha);
  return clamped.toString({ format: 'hex' });
};

const normaliseHex = (input: string): string => {
  try {
    const color = new Color(input);
    return toClampedSrgbHex(color);
  } catch {
    throw new Error(`Invalid color value: ${input}`);
  }
};

const lerp = (start: number, end: number, t: number) => start + (end - start) * t;

const shortestHueDelta = (start: number, end: number) => {
  const delta = ((end - start + 180) % 360) - 180;
  return delta === -180 ? 180 : delta;
};

const safeHue = (hue: number | undefined, fallback: number) => {
  if (!Number.isFinite(hue)) {
    return fallback;
  }
  return hue as number;
};

export function interpolateGradientColor(startHex: string, endHex: string, t: number, space: GradientSpace = 'oklch'): string {
  const startColor = new Color(startHex);
  const endColor = new Color(endHex);

  if (space === 'oklab') {
    const startOklab = startColor.to('oklab');
    const endOklab = endColor.to('oklab');
    const [l1, a1, b1] = startOklab.coords;
    const [l2, a2, b2] = endOklab.coords;
    const interpolated = new Color('oklab', [
      lerp(l1, l2, t),
      lerp(a1, a2, t),
      lerp(b1, b2, t),
    ]);
    return toClampedSrgbHex(interpolated);
  }

  const startOklch = startColor.to('oklch');
  const endOklch = endColor.to('oklch');
  const [l1, c1, h1Raw] = startOklch.coords;
  const [l2, c2, h2Raw] = endOklch.coords;
  const fallbackHue = Number.isFinite(h1Raw) ? h1Raw : h2Raw;
  const startHue = safeHue(h1Raw, fallbackHue ?? 0);
  const endHue = safeHue(h2Raw, startHue);
  const hueDelta = shortestHueDelta(startHue, endHue);
  const hue = startHue + hueDelta * t;
  const lightness = lerp(l1, l2, t);
  const chroma = Math.max(lerp(c1, c2, t), 0);
  const interpolated = new Color('oklch', [lightness, chroma, hue]);
  return toClampedSrgbHex(interpolated);
}

const normaliseStops = (stops: GradientStopInput[]): GradientStop[] => {
  const normalised = stops.map((stop, index) => ({
    id: stop.id ?? `stop-${index}`,
    position: clamp01(stop.position),
    color: normaliseHex(stop.color),
  } satisfies GradientStop));

  normalised.sort((a, b) => {
    if (a.position === b.position) {
      return a.id.localeCompare(b.id);
    }
    return a.position - b.position;
  });

  return normalised;
};

const sampleGradient = (stops: GradientStop[], space: GradientSpace, sampleCount: number): string[] => {
  const samples: string[] = [];

  if (stops.length === 0) {
    return samples;
  }

  for (let index = 0; index < sampleCount; index++) {
    const globalT = sampleCount === 1 ? 0 : index / (sampleCount - 1);

    if (globalT <= stops[0].position) {
      samples.push(stops[0].color);
      continue;
    }

    const lastStop = stops[stops.length - 1];
    if (globalT >= lastStop.position) {
      samples.push(lastStop.color);
      continue;
    }

    let leftIndex = 0;
    let rightIndex = stops.length - 1;

    for (let i = 0; i < stops.length - 1; i++) {
      const current = stops[i];
      const next = stops[i + 1];
      if (globalT >= current.position && globalT <= next.position) {
        leftIndex = i;
        rightIndex = i + 1;
        break;
      }
    }

    const left = stops[leftIndex];
    const right = stops[rightIndex];
    const span = right.position - left.position;
    const localT = span === 0 ? 0 : (globalT - left.position) / span;
    samples.push(interpolateGradientColor(left.color, right.color, clamp01(localT), space));
  }

  return samples;
};

const buildCssGradient = (stops: GradientStop[]): string => {
  const segments = stops.map((stop) => `${stop.color} ${(stop.position * 100).toFixed(2)}%`);
  return `linear-gradient(90deg, ${segments.join(', ')})`;
};

const buildSvgGradient = (stops: GradientStop[], options: Required<Pick<GradientBuildOptions, 'width' | 'height'>> & { gradientId: string }): string => {
  const stopMarkup = stops
    .map((stop) => `    <stop offset="${(stop.position * 100).toFixed(2)}%" stop-color="${stop.color}" />`)
    .join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>\n` +
    `<svg xmlns="http://www.w3.org/2000/svg" width="${options.width}" height="${options.height}" viewBox="0 0 ${options.width} ${options.height}">\n` +
    `  <defs>\n` +
    `    <linearGradient id="${options.gradientId}" x1="0%" y1="0%" x2="100%" y2="0%">\n` +
    `${stopMarkup}\n` +
    `    </linearGradient>\n` +
    `  </defs>\n` +
    `  <rect x="0" y="0" width="100%" height="100%" fill="url(#${options.gradientId})" />\n` +
    `</svg>`;
};

export function buildGradientDefinition(stopsInput: GradientStopInput[], options: GradientBuildOptions = {}): GradientBuildResult {
  if (!Array.isArray(stopsInput) || stopsInput.length < 2) {
    throw new Error('At least two gradient stops are required.');
  }

  const stops = normaliseStops(stopsInput);
  const space = options.space ?? 'oklch';
  const sampleCount = Math.max(2, Math.min(options.sampleCount ?? DEFAULT_SAMPLE_COUNT, 256));
  const samples = sampleGradient(stops, space, sampleCount);
  const css = buildCssGradient(stops);
  const gradientId = options.gradientId ?? 'chromis-gradient';
  const svg = buildSvgGradient(stops, {
    width: options.width ?? DEFAULT_WIDTH,
    height: options.height ?? DEFAULT_HEIGHT,
    gradientId,
  });

  return {
    stops,
    css,
    svg,
    samples,
  } satisfies GradientBuildResult;
}
